import { useState, type ReactNode } from "react";
import { ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ProductSelectItems } from "@/components/ProductSelectItems";
import { formatCentsAsUsd } from "@/lib/money";
import type { ProductCatalogOption } from "@/types/ProductCatalog";
import {
  normalizePageOnCheckoutSuccess,
  type PageCheckoutLineItem,
  type PageCheckoutSettings,
  type PageOnCheckoutSuccessAction,
} from "@/types/pageFormSubmit";

export type CheckoutSetupValue = {
  lineItems?: PageCheckoutLineItem[];
  onCheckoutSuccess?: PageOnCheckoutSuccessAction[];
  checkoutSettings?: PageCheckoutSettings;
};

type PageOption = {
  _id: string;
  title?: string;
  route?: string;
};

type AutomationOption = {
  _id: string;
  name?: string;
};

const ABANDONED_NONE = "__none__";

function abandonedAutomationIdValue({
  value,
}: {
  value: PageCheckoutSettings["onAbandonedAutomationId"];
}): string {
  if (value == null) return "";
  return String(value);
}

function AdvancedAccordion({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="pt-2">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        {open ? (
          <ChevronDown className="h-3 w-3 shrink-0" />
        ) : (
          <ChevronRight className="h-3 w-3 shrink-0" />
        )}
        Advanced
      </button>
      {open ? <div className="space-y-2 mt-2">{children}</div> : null}
    </div>
  );
}

export function CheckoutSetupFields({
  idPrefix,
  value,
  onChange,
  products,
  pages,
  automations,
  isLoadingProducts,
  isLoadingPages,
  isLoadingAutomations,
  showSaveCard = true,
  collapseAdvanced = false,
}: {
  idPrefix: string;
  value: CheckoutSetupValue;
  onChange: (next: CheckoutSetupValue) => void;
  products: ProductCatalogOption[];
  pages: PageOption[];
  automations: AutomationOption[];
  isLoadingProducts?: boolean;
  isLoadingPages?: boolean;
  isLoadingAutomations?: boolean;
  showSaveCard?: boolean;
  collapseAdvanced?: boolean;
}) {
  const lineItems = value.lineItems || [];
  const checkoutSettings = value.checkoutSettings ?? {};
  const successList = normalizePageOnCheckoutSuccess(value.onCheckoutSuccess);
  const abandonedId = abandonedAutomationIdValue({
    value: checkoutSettings.onAbandonedAutomationId,
  });

  const emit = (patch: CheckoutSetupValue) => {
    onChange({
      lineItems: value.lineItems,
      onCheckoutSuccess: value.onCheckoutSuccess,
      checkoutSettings: value.checkoutSettings,
      ...patch,
    });
  };

  const emitSettings = (patch: PageCheckoutSettings) => {
    const nextSettings: PageCheckoutSettings = {
      ...checkoutSettings,
      ...patch,
    };
    const hasSettings =
      nextSettings.savePaymentMethod === true ||
      Boolean(nextSettings.submitBtnText?.trim()) ||
      Boolean(nextSettings.paymentMethodConfigurationId?.trim()) ||
      Boolean(abandonedAutomationIdValue({ value: nextSettings.onAbandonedAutomationId })) ||
      Boolean(nextSettings.tipping?.title?.trim()) ||
      Boolean(nextSettings.tipping?.description?.trim()) ||
      Boolean(nextSettings.contactCustomFields?.length) ||
      Boolean(nextSettings.paymentMethodTypes?.length) ||
      Boolean(nextSettings.shipmentNotificationEmail);

    emit({
      checkoutSettings: hasSettings ? nextSettings : undefined,
    });
  };

  const advancedFields = (
    <>
      {showSaveCard ? (
        <div className="flex items-center gap-2 pt-2">
          <Checkbox
            id={`${idPrefix}-save-card`}
            checked={checkoutSettings.savePaymentMethod === true}
            onCheckedChange={(checked) => {
              emitSettings({
                savePaymentMethod: checked === true ? true : undefined,
              });
            }}
          />
          <Label
            htmlFor={`${idPrefix}-save-card`}
            className="text-xs font-normal cursor-pointer"
          >
            Save card only (charge later)
          </Label>
        </div>
      ) : null}

      <div className="space-y-2 pt-2 border-t">
        <Label className="text-xs">Payment button text</Label>
        <Input
          value={checkoutSettings.submitBtnText ?? ""}
          onChange={(event) => {
            const trimmed = event.target.value.trim();
            emitSettings({
              submitBtnText: trimmed || undefined,
            });
          }}
          placeholder="Place Order"
        />
      </div>

      <div className="space-y-2 pt-2 border-t">
        <Label className="text-xs">Payment method configuration ID</Label>
        <Input
          value={checkoutSettings.paymentMethodConfigurationId ?? ""}
          onChange={(event) => {
            const trimmed = event.target.value.trim();
            emitSettings({
              paymentMethodConfigurationId: trimmed || undefined,
            });
          }}
          placeholder="pmc_…"
        />
      </div>

      <div className="space-y-2 pt-2 border-t">
        <Label className="text-xs">On abandoned checkout</Label>
        <Select
          value={abandonedId || ABANDONED_NONE}
          onValueChange={(v) => {
            emitSettings({
              onAbandonedAutomationId:
                v === ABANDONED_NONE ? undefined : v,
            });
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue
              placeholder={isLoadingAutomations ? "Loading..." : "None"}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ABANDONED_NONE}>None</SelectItem>
            {automations.map((automation) => (
              <SelectItem key={automation._id} value={automation._id}>
                {automation.name || automation._id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2 pt-2 border-t">
        <Label className="text-xs">Tip prompt override</Label>
        <p className="text-xs text-muted-foreground">
          Optional per-checkout override for the tip checkbox label and description.
        </p>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Checkbox label</Label>
          <Input
            value={checkoutSettings.tipping?.title ?? ""}
            onChange={(event) => {
              const title = event.target.value.trim();
              const nextTipping = {
                ...(checkoutSettings.tipping ?? {}),
                ...(title ? { title } : { title: undefined }),
              };
              const hasTipping =
                Boolean(nextTipping.title?.trim()) ||
                Boolean(nextTipping.description?.trim());
              emitSettings({
                tipping: hasTipping ? nextTipping : undefined,
              });
            }}
            placeholder="Add tip"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Description</Label>
          <Textarea
            value={checkoutSettings.tipping?.description ?? ""}
            onChange={(event) => {
              const description = event.target.value;
              const nextTipping = {
                ...(checkoutSettings.tipping ?? {}),
                ...(description.trim() !== ""
                  ? { description }
                  : { description: undefined }),
              };
              const hasTipping =
                Boolean(nextTipping.title?.trim()) ||
                Boolean(nextTipping.description?.trim());
              emitSettings({
                tipping: hasTipping ? nextTipping : undefined,
              });
            }}
            placeholder="Shown when the customer opts in to add a tip"
            rows={3}
          />
        </div>
      </div>

      <div className="space-y-2 pt-2 border-t">
        <Label className="text-xs">On Checkout Success</Label>
        {successList.map((succ, si) => (
          <div key={si} className="space-y-2 border border-gray-100 rounded-md p-2">
            <div className="flex gap-2 items-center">
              <Select
                value={succ.type}
                onValueChange={(v) => {
                  const next = [...successList];
                  if (v === "GoTo") {
                    next[si] = { type: "GoTo", pageId: "" };
                  } else {
                    next[si] = { type: "StartAutomation", automationId: "" };
                  }
                  emit({ onCheckoutSuccess: next });
                }}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GoTo">Go to page</SelectItem>
                  <SelectItem value="StartAutomation">Start flow</SelectItem>
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => {
                  const next = successList.filter((_, i) => i !== si);
                  emit({
                    onCheckoutSuccess: next.length ? next : undefined,
                  });
                }}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
            {succ.type === "GoTo" ? (
              <Select
                value={succ.pageId || ""}
                onValueChange={(v) => {
                  const next = [...successList];
                  next[si] = { type: "GoTo", pageId: v };
                  emit({ onCheckoutSuccess: next });
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={isLoadingPages ? "Loading..." : "Select page"}
                  />
                </SelectTrigger>
                <SelectContent>
                  {pages.map((page) => (
                    <SelectItem key={page._id} value={page._id}>
                      {page.title} ({page.route})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Select
                value={succ.automationId || ""}
                onValueChange={(v) => {
                  const next = [...successList];
                  next[si] = { type: "StartAutomation", automationId: v };
                  emit({ onCheckoutSuccess: next });
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={
                      isLoadingAutomations ? "Loading..." : "Select flow"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {automations.map((automation) => (
                    <SelectItem key={automation._id} value={automation._id}>
                      {automation.name || automation._id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            emit({
              onCheckoutSuccess: [
                ...successList,
                { type: "GoTo", pageId: "" },
              ],
            });
          }}
        >
          <Plus className="w-3 h-3 mr-1" />
          Add success action
        </Button>
      </div>
    </>
  );

  return (
    <div className="space-y-2">
      <Label className="text-xs">Products</Label>
      <div className="space-y-2">
        {lineItems.map((line, pi) => {
          const productId = line.productId || "";
          const selectedProduct = products.find((p) => p._id === productId);
          const variantOptions = selectedProduct?.variants || [];
          const variantValue = line.variantId?.trim() || "__base__";

          return (
            <div key={pi} className="space-y-2 border border-gray-100 rounded-md p-2">
              <div className="flex gap-2 items-center">
                <Select
                  value={productId || ""}
                  onValueChange={(v) => {
                    const lines = [...lineItems];
                    lines[pi] = { productId: v, variantId: undefined };
                    emit({ lineItems: lines });
                  }}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue
                      placeholder={isLoadingProducts ? "Loading..." : "Select product"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <ProductSelectItems
                      products={products}
                      selectedProductId={productId || undefined}
                      getLabel={(product) =>
                        product.name || `Product (${product.type || "unknown"})`
                      }
                    />
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    const lines = lineItems.filter((_, i) => i !== pi);
                    emit({ lineItems: lines.length ? lines : undefined });
                  }}
                  className="h-8 w-8 shrink-0"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
              {productId && variantOptions.length > 0 ? (
                <div className="space-y-1 pl-1">
                  <Label className="text-xs text-muted-foreground">Variant</Label>
                  <Select
                    value={variantValue}
                    onValueChange={(v) => {
                      const lines = [...lineItems];
                      lines[pi] = {
                        ...lines[pi],
                        variantId: v === "__base__" ? undefined : v,
                      };
                      emit({ lineItems: lines });
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Default price" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__base__">Default (product price)</SelectItem>
                      {variantOptions.map((variant) => (
                        <SelectItem key={variant._id} value={variant._id}>
                          {variant.name}
                          {variant.price != null
                            ? ` — $${formatCentsAsUsd(variant.price)}`
                            : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
            </div>
          );
        })}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            emit({ lineItems: [...lineItems, { productId: "" }] });
          }}
        >
          <Plus className="w-3 h-3 mr-1" />
          Add product
        </Button>
      </div>

      {collapseAdvanced ? (
        <AdvancedAccordion>{advancedFields}</AdvancedAccordion>
      ) : (
        advancedFields
      )}
    </div>
  );
}
